import uniq from 'lodash/uniq';
import { useCallback, useEffect } from 'react';

import {
  canUseLocalStorage,
  getItemFromLocalStorage,
  localStorageCache,
  removeItemFromLocalStorage,
  setItemInLocalStorage,
} from '@/utility/localstorage';
import { useReactiveVar } from '@/utility/reactiveVariable';

let pollerId: ReturnType<typeof setInterval> | undefined;
const subscribers: string[] = [];

/**
 * Technically private, but exported so tests can reset any polling
 */
export const teardown = () => {
  if (pollerId) {
    clearInterval(pollerId);
    pollerId = undefined;
  }
};

export function useLocalStorage(
  key: string,
): [string | null, { update: (value: string | null) => void; remove: () => void }] {
  const cache = useReactiveVar(localStorageCache);
  const update = useCallback(
    (value: string | null) => setItemInLocalStorage(key, value || ''),
    [key],
  );
  const remove = useCallback(() => {
    removeItemFromLocalStorage(key);
  }, [key]);

  useEffect(() => {
    getItemFromLocalStorage(key);
    subscribers.push(key);

    if (!pollerId && canUseLocalStorage() && subscribers.length > 0) {
      pollerId = setInterval(() => {
        uniq(subscribers).forEach(key => {
          getItemFromLocalStorage(key);
        });
      }, 2000);
    }

    return () => {
      subscribers.splice(subscribers.indexOf(key), 1);
      if (pollerId && subscribers.length === 0) {
        teardown();
      }
    };
  }, [key]);

  return [cache[key] || null, { update, remove }];
}
